import { Button, Cascader, Col, Divider, Input, Row, Select, Tag } from "antd";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AppContext from "../../../context/app/context";
import StoreContext from "../../../context/store/context";
import StoreContextProvider from "../../../context/store/provider";
import { ICity } from "../../../models/city/response";
import {
  getStorStatus,
  getStorType,
  getWorkStatus,
} from "../../../models/store/enum";
import { IUpdateStore } from "../../../models/store/request";
import EndPoints from "../../../services/end-points";
import {
  MAP_DEFAULT_LOCATION,
  languages,
} from "../../../utils/helpers/constants";
import { execute } from "../../../utils/helpers/execute";
import { getTranslations } from "../../../utils/helpers/translations";
import Controller from "../../form-components/controller";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import FormItem from "../../general/form-item";
import Marker from "../../general/map/marker/index";
import styles from "./style.module.scss";
import { MediumFor } from "../../../models/medium/enum";
import CategoryContext from "../../../context/category/context";
import { IChildCat } from "../../../models/category/request";
import { ICategory } from "../../../models/category/response";
import { ITag } from "../../../models/tag/response";
import { IMedium } from "../../../models/medium/response";
import ImageFieldBlock from "../../general/image-field-block";
import {
  errorNotification,
  successNotification,
} from "../../../utils/helpers/notification";
import { appendImageProcessingFields, getImageProcessingMode, hasPendingImageUpload, resolveUploadFile } from "../../../utils/helpers/media-upload";

const Option = Select.Option;

const Form = () => {
  const { details, actions, loading } = useContext(StoreContext);
  const { direction } = useContext(AppContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const storeStatus = [1, 2];
  const storeTypes = [1];
  const [cities, setCities] = useState<ICity[]>([]);
  const [tags, setTags] = useState<ITag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [operationStoreCategories, setOperationStoreCategories] = useState<
    ITag[]
  >([]);
  const [operationStoreCategoriesLoading, setOperationStoreCategoriesLoading] =
    useState(true);
  const [tagKeyword, setTagKeyword] = useState<string>();

  const [newImages, setNewImages] = useState<Record<string, string | null>>({});
  const [newImageFiles, setNewImageFiles] = useState<Record<string, File | undefined>>({});
  const [originalImageFiles, setOriginalImageFiles] = useState<Record<string, File | undefined>>({});
  const [imageChanged, setImageChanged] = useState<Record<string, boolean>>({});
  const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});
  const [withoutBackgrounds, setWithoutBackgrounds] = useState<
    Record<string, boolean>
  >({});
  const [preserveOriginals, setPreserveOriginals] = useState<Record<string, boolean>>({});

  const handleImageChange =
    (fieldName: string) => (response: IMedium | null) => {
      setNewImages((prev) => ({ ...prev, [fieldName]: response?.url ?? null }));
      setNewImageFiles((prev) => ({ ...prev, [fieldName]: response?.file }));
      setOriginalImageFiles((prev) => ({ ...prev, [fieldName]: response?.originalFile }));
      setImageChanged((prev) => ({ ...prev, [fieldName]: Boolean(response) }));
    };

  const handleUploadNewImage = async (fieldName: string) => {
    const newImageUrl = newImages[fieldName];
    if (!newImageUrl) return;

    try {
      const preserveOriginal = preserveOriginals[fieldName] ?? false;
      const file = await resolveUploadFile({
        imageUrl: newImageUrl,
        processedFile: newImageFiles[fieldName],
        originalFile: originalImageFiles[fieldName],
        preserveOriginal,
        fallbackName: fieldName,
      });

      const formData = new FormData();
      formData.append("medium", file);
      formData.append("type", "1");
      formData.append("for", MediumFor.UserProfile);
      appendImageProcessingFields(
        formData,
        getImageProcessingMode(withoutBackgrounds[fieldName] ?? false, preserveOriginal),
      );

      const uploadRes = await EndPoints.media.uploadMedia(formData);
      setValue(fieldName, uploadRes.id, { shouldDirty: true });
      setImageChanged((prev) => ({ ...prev, [fieldName]: false }));
      setNewImages((prev) => ({ ...prev, [fieldName]: uploadRes?.url ?? newImageUrl }));
      setNewImageFiles((prev) => ({ ...prev, [fieldName]: undefined }));
      setOriginalImageFiles((prev) => ({ ...prev, [fieldName]: undefined }));
      setPreserveOriginals((prev) => ({ ...prev, [fieldName]: false }));
      successNotification("تم رفع الصورة بنجاح");
    } catch (err) {
      console.error("Upload failed", err);
      errorNotification("حدث خطأ اثناء رفع الصورة");
    }
  };

  const handleDeleteImage = async (fieldName: string) => {
    try {
      setImageLoading((prev) => ({ ...prev, [fieldName]: true }));
      setValue(fieldName, null);
    } finally {
      setImageLoading((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleRemoveNewImage = async (fieldName: string, restoreCurrentImage = true) => {
    const currentImage = restoreCurrentImage
      ? fieldName === "logoId" ? details?.logo : details?.image
      : undefined;

    setValue(fieldName, currentImage?.id ?? undefined, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    await trigger(fieldName);
    setImageChanged((prev) => ({ ...prev, [fieldName]: false }));
    setNewImages((prev) => ({ ...prev, [fieldName]: null }));
    setNewImageFiles((prev) => ({ ...prev, [fieldName]: undefined }));
    setOriginalImageFiles((prev) => ({ ...prev, [fieldName]: undefined }));
    setPreserveOriginals((prev) => ({ ...prev, [fieldName]: false }));
    setWithoutBackgrounds((prev) => ({
      ...prev,
      [fieldName]: Boolean(currentImage?.without_background),
    }));
  };

  const {
    allCategories,
    actions: { getAllData },
  } = useContext(CategoryContext);

  const [filteredChildCategories, setFilteredChildCategories] = useState<
    IChildCat[]
  >([]);

  const [citiesLoading, setCitiesLoading] = useState(true);
  const methods = useForm<any>({
    defaultValues: {
      pharmacyPromotion: details?.pharmacyPromotion ?? false,
      isNamlieah: details?.isNamlieah ?? false,
      activatingPublicService: details?.activatingPublicService ?? false,
      freeDelivery: details?.freeDelivery ?? false,
      hasOffers: details?.hasOffers ?? false,
      tagIds: details?.tags?.map((tag) => tag?.id),
      acceptedExchange: details?.acceptedExchange ?? false,
      location: details?.location ?? {
        type: "Point",
        coordinates: [1, 2],
      },
      childCategories: filteredChildCategories ?? [],
      operationStoreCategoryId: details?.operationStoreCategory?.id,
    } as IUpdateStore,
  });

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = methods;

  useEffect(() => {
    const getCities = async () => {
      try {
        const { data } = await EndPoints.city.getAllCities({
          light: true,
          perPage: -1,
        });
        setCities(data.data);
        setCitiesLoading(false);
      } catch (error) {
      } finally {
        setCitiesLoading(false);
      }
    };
    getCities();
    !allCategories && getAllData();
  }, [direction]);

  useEffect(() => {
    const getTags = async () => {
      try {
        const { data } = await EndPoints.tag.getAllTags({
          light: true,
          perPage: -1,
        });
        setTags(data.data);
      } catch (error) {
      } finally {
        setTagsLoading(false);
      }
    };
    getTags();
  }, [direction]);

  useEffect(() => {
    const getOperationStoreCategories = async () => {
      try {
        const { data } =
          await EndPoints.operationStoreCategories.getAllOperationStoreCategories(
            {
              light: true,
              perPage: -1,
            },
          );
        setOperationStoreCategories(data.data);
      } catch (error) {
      } finally {
        setOperationStoreCategoriesLoading(false);
      }
    };
    getOperationStoreCategories();
  }, [direction]);

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  useEffect(() => {
    const formattedChildCategories = details?.childCategories?.map((cat) => [
      cat.grand?.id || null,
      cat.parent?.id || null,
      cat.id || null,
    ]);

    const filteredCategoriesForAPI = details?.childCategories?.map((cat) => ({
      id: cat?.id,
      name: cat?.name,
      percent: cat?.percent,
    }));

    methods.reset({
      pharmacyPromotion: details?.pharmacyPromotion ?? false,
      isNamlieah: details?.isNamlieah ?? false,
      address: details?.address,
      cityId: details?.city?.id,
      firstPhone: details?.firstPhone,
      tagIds: details?.tags?.map((tag) => tag?.id),
      note: getTranslations(details?.translations?.note) ?? null,
      name: getTranslations(details?.translations?.name),
      secondPhone: details?.secondPhone,
      status: details?.status,
      type: details?.type,
      freeDelivery: details?.freeDelivery,
      activatingPublicService: details?.activatingPublicService ?? false,
      hasOffers: details?.hasOffers,
      endWorkingTime: details?.endWorkingTime,
      acceptedExchange: details?.acceptedExchange ?? false,
      maxOrderValueForFreeDelivery: details?.maxOrderValueForFreeDelivery,

      minOrderValueForFreeDelivery: details?.minOrderValueForFreeDelivery,

      startWorkingTime: details?.startWorkingTime,
      imageId: details?.image?.id,
      logoId: details?.logo?.id,
      workStatus: details?.workStatus,
      childCategories: formattedChildCategories ?? [],
      visibilityRadiusMeters: details?.visibilityRadiusMeters,
      operationStoreCategoryId: details?.operationStoreCategory?.id,
    });

    setFilteredChildCategories(filteredCategoriesForAPI);

    setWithoutBackgrounds({
      logoId: details?.logo?.without_background,
      imageId: details?.image?.without_background,
    });
  }, [details, methods]);

  interface Option {
    value: number;
    label: string;
    children?: Option[];
  }

  const handlePercentChange = (id: number, newPercent: number) => {
    setFilteredChildCategories((prevCategories) =>
      prevCategories.map((cat) =>
        cat.id === id ? { ...cat, percent: newPercent } : cat,
      ),
    );
  };

  const buildNestedOptions = (allCategories: any[]): Option[] => {
    return allCategories
      ?.filter((cat) => cat.parent === null && cat.grand === null)
      .map((grand) => ({
        value: grand.id,
        label: grand.name,
        children: allCategories
          ?.filter(
            (cat) => cat.grand?.id === grand.id && cat.parent?.id == null,
          )
          .map((parent) => ({
            value: parent.id,
            label: parent.name,
            children: allCategories
              ?.filter((cat) => cat.parent?.id === parent.id)
              .map((child) => ({
                value: child.id,
                label: child.name,
                percent: child.percent ?? 0,
              })),
          })),
      }));
  };

  const handleChange = (values: number[][]) => {
    let selectedCategories: IChildCat[] = [];
    let categories: ICategory[] = [];

    values.forEach((element) => {
      if (element.length === 1) {
        categories.push(
          ...(allCategories?.filter(
            (cat) =>
              cat.parent !== null && cat.grand && cat.grand.id === element[0],
          ) || []),
        );
      } else if (element.length === 2) {
        categories.push(
          ...(allCategories?.filter(
            (cat) => cat.parent && cat.parent.id === element[1],
          ) || []),
        );
      } else if (element.length === 3) {
        categories.push(
          ...(allCategories?.filter((cat) => cat.id === element[2]) || []),
        );
      }
    });

    selectedCategories = categories.map((cat) => {
      const existingCat = filteredChildCategories?.find(
        (fc) => fc?.id === cat?.id,
      );
      return {
        id: cat?.id,
        name: cat?.name,
        percent: existingCat?.percent ?? cat.percent ?? 0,
      } as IChildCat;
    });
    setFilteredChildCategories(selectedCategories);
  };

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const onSubmit = async (data: any) => {
    if (hasPendingImageUpload(imageChanged, newImages)) {
      errorNotification(t("pending_image_upload_message"));
      return;
    }

    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        details
          ? await EndPoints.store.updateStore(details?.id, {
              ...data,
              childCategories: filteredChildCategories,
              location: {
                type: "Point",
                coordinates: data.map
                  ? [data.map.lng, data.map.lat]
                  : [
                      details?.location?.coordinates[0],
                      details?.location?.coordinates[1],
                    ],
              },
            })
          : await EndPoints.store.createStore({
              ...data,
              childCategories: filteredChildCategories,
              location: {
                type: "Point",
                coordinates: data.map
                  ? [data.map.lng, data.map.lat]
                  : [MAP_DEFAULT_LOCATION.lng, MAP_DEFAULT_LOCATION.lat],
              },
            });
        navigate(-1);
      },
      fallback: (error) => {},
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  const pharmacyPromotion = useWatch({ control, name: "pharmacyPromotion" });
  const freeDelivery = useWatch({ control, name: "freeDelivery" });
  const childCategories = useWatch({
    control,
    name: "childCategories",
  });
  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details ? t("update_store") : t("add_store")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div className={styles.children}>
              <Row gutter={[16, 8]}>
                {languages?.map((lang, index) => {
                  return (
                    <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                      <FieldBuilder
                        key={index}
                        name={`name[${lang?.code}]`}
                        label={`${t("name")} (${lang?.name})`}
                        input={{ type: "text" }}
                        rules={{ required: true }}
                      />
                    </Col>
                  );
                })}

                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    name="firstPhone"
                    label={t("first_phone")}
                    input={{ type: "number" }}
                    rules={{ required: false }}
                  />
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    name="secondPhone"
                    label={t("second_phone")}
                    input={{ type: "number" }}
                    rules={{ required: false }}
                  />
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    name="visibilityRadiusMeters"
                    label={t("visibility_radius_meters")}
                    input={{ type: "number" }}
                    suffix="m"
                    rules={{ required: pharmacyPromotion }}
                  />
                </Col>

                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    name="maxOrderValueForFreeDelivery"
                    label={t("max_order_value_for_free_delivery")}
                    input={{ type: "number" }}
                    suffix="m"
                    rules={{ required: false }}
                  />
                </Col>

                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    name="minOrderValueForFreeDelivery"
                    label={t("min_order_value_for_free_delivery")}
                    input={{ type: "number" }}
                    suffix="m"
                    rules={{ required: pharmacyPromotion && freeDelivery }}
                  />
                </Col>

                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    name="status"
                    label={`${t("status")}`}
                    width="large"
                    rules={{ required: true }}
                    input={{
                      type: "select",
                      loading: false,
                      options: storeStatus?.map((status, index) => {
                        return {
                          key: index,
                          label: t(getStorStatus(status)),
                          value: status,
                        };
                      }),
                    }}
                  />
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    name="type"
                    label={`${t("type")}`}
                    width="large"
                    rules={{ required: true }}
                    input={{
                      type: "select",
                      loading: false,
                      options: storeTypes?.map((type, index) => {
                        return {
                          key: index,
                          label: t(getStorType(type)),
                          value: type,
                        };
                      }),
                    }}
                  />
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    name="workStatus"
                    label={`${t("current_status")}`}
                    width="large"
                    rules={{ required: true }}
                    input={{
                      type: "select",
                      loading: false,
                      options: [1, 2]?.map((type, index) => {
                        return {
                          key: index,
                          label: t(getWorkStatus(type)),
                          value: type,
                        };
                      }),
                    }}
                  />
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    name="cityId"
                    label={`${t("city")}`}
                    width="large"
                    rules={{ required: true }}
                    input={{
                      type: "select",
                      loading: citiesLoading,
                      allowSearch: true,
                      options: cities?.map((city, index) => {
                        return {
                          key: index,
                          label: city.name,
                          value: city.id,
                        };
                      }),
                    }}
                  />
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FormItem label={t("tags")}>
                    <Controller
                      name="tagIds"
                      control={control}
                      rules={{ required: false }}
                      render={({ field: { ...field } }) => {
                        return (
                          <Select
                            loading={tagsLoading}
                            {...field}
                            onSearch={(val) => {
                              setTagKeyword(val);
                            }}
                            style={{ width: "100%" }}
                            allowClear
                            mode="multiple"
                            placeholder={t("tags")}
                            filterOption={false}
                          >
                            {tags
                              .filter((tr) =>
                                tagKeyword
                                  ? tr?.name?.includes(tagKeyword)
                                  : true,
                              )
                              .map((tr, index) => {
                                return (
                                  <Option
                                    key={index}
                                    value={tr?.id}
                                    label={
                                      <Tag color={tr?.color}>{tr.name}</Tag>
                                    }
                                  >
                                    <Tag color={tr?.color}>{tr?.name}</Tag>
                                  </Option>
                                );
                              })}
                          </Select>
                        );
                      }}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    name="operationStoreCategoryId"
                    label={`${t("operation_store_categories")}`}
                    width="large"
                    rules={{ required: false }}
                    input={{
                      type: "select",
                      loading: operationStoreCategoriesLoading,
                      allowSearch: true,
                      options: operationStoreCategories?.map(
                        (operationStoreCategory, index) => {
                          return {
                            key: index,
                            label: operationStoreCategory.name,
                            value: operationStoreCategory.id,
                          };
                        },
                      ),
                    }}
                  />
                </Col>
                {/* ------------------------------ */}
                {(!id ||
                  (id &&
                    filteredChildCategories &&
                    filteredChildCategories.length == 0)) && (
                  <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                    <FormItem label={t("categories")} required>
                      <Controller
                        name="childCategories"
                        control={control}
                        rules={{ required: false }}
                        render={({ field: { ...field } }) => {
                          return (
                            <Cascader
                              style={{ width: "100%" }}
                              showSearch
                              allowClear
                              placeholder="اختر الفئات"
                              multiple
                              options={buildNestedOptions(allCategories)}
                              onChange={handleChange}
                            />
                          );
                        }}
                      />
                    </FormItem>
                  </Col>
                )}
                {id &&
                  filteredChildCategories &&
                  filteredChildCategories.length > 0 && (
                    <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                      <FormItem label={t("categories")} required>
                        <Controller
                          name="childCategories"
                          control={control}
                          rules={{ required: false }}
                          render={({ field: { ...field } }) => {
                            return (
                              <Cascader
                                style={{ width: "100%" }}
                                showSearch
                                allowClear
                                placeholder="اختر الفئات"
                                multiple
                                options={buildNestedOptions(allCategories)}
                                onChange={handleChange}
                                defaultValue={childCategories}
                              />
                            );
                          }}
                        />
                      </FormItem>
                    </Col>
                  )}

                {/* ------------------------------ */}
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    label={`${t("start_working_time")}`}
                    name="startWorkingTime"
                    rules={{
                      required: true,
                    }}
                    input={{ type: "time-picker", format: "HH:mm" }}
                  />
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    label={`${t("end_working_time")}`}
                    name="endWorkingTime"
                    rules={{
                      required: true,
                    }}
                    input={{ type: "time-picker", format: "HH:mm" }}
                  />
                </Col>
                <Col span={24}>
                  {filteredChildCategories &&
                    filteredChildCategories.length > 0 && (
                      <div style={{ margin: "10px 0" }}>
                        <Row gutter={[16, 16]}>
                          {filteredChildCategories.map((cat) => (
                            <Col key={cat.id} xs={24} sm={12} lg={8} xl={8}>
                              <div
                                style={{
                                  border: "1px solid #d9d9d9",
                                  borderRadius: "8px",
                                  padding: "16px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: 500,
                                    fontSize: "16px",
                                  }}
                                >
                                  {cat.name}
                                </span>
                                <Input
                                  value={cat.percent}
                                  type="number"
                                  placeholder={`${cat.name} (%)`}
                                  style={{ width: "80px", textAlign: "center" }}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    handlePercentChange(
                                      cat.id,
                                      isNaN(value) ? 0 : value,
                                    );
                                  }}
                                />
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    label={`${t("promotion_pharmacy")}`}
                    name="pharmacyPromotion"
                    input={{ type: "checkBox" }}
                  />
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    label={`${t("is_namlieah")}`}
                    name="isNamlieah"
                    input={{ type: "checkBox" }}
                  />
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    label={`${t("activating_public_service")}`}
                    name="activatingPublicService"
                    input={{ type: "checkBox" }}
                  />
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    label={`${t("free_delivery")}`}
                    name="freeDelivery"
                    input={{ type: "checkBox" }}
                  />
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                  <FieldBuilder
                    label={`${t("accepted_exchange")}`}
                    name="acceptedExchange"
                    input={{ type: "checkBox" }}
                  />
                </Col>
                <Col span={6}>
                  <FieldBuilder
                    label={`${t("has_offers")}`}
                    name="hasOffers"
                    input={{ type: "checkBox" }}
                  />
                </Col>
                <Col span={24}>
                  <FieldBuilder
                    name="address"
                    label={t("store_address")}
                    input={{ type: "text-area" }}
                    rules={{ required: true }}
                  />
                </Col>
                {languages?.map((lang, index) => {
                  return (
                    <Col span={12}>
                      <FieldBuilder
                        key={index}
                        name={`note[${lang?.code}]`}
                        label={`${t("note")} (${lang?.name})`}
                        input={{ type: "text-area" }}
                        rules={{ required: false }}
                      />
                    </Col>
                  );
                })}
                <Col span={24}>
                  <ImageFieldBlock
                    fieldName="imageId"
                    currentId={details?.image?.id}
                    fieldValue={withoutBackgrounds["imageId"]}
                    currentImage={details?.image}
                    newImageUrl={newImages["imageId"] ?? null}
                    isChanged={imageChanged["imageId"]}
                    onUpload={() => handleUploadNewImage("imageId")}
                    onImageChange={handleImageChange("imageId")}
                    onRemoveNewImage={(restoreCurrentImage) => handleRemoveNewImage("imageId", restoreCurrentImage)}
                    setWithoutBackgrounds={(val) =>
                      setWithoutBackgrounds((prev) => ({
                        ...prev,
                        imageId: val,
                      }))
                    }
                    preserveOriginal={preserveOriginals["imageId"]}
                    setPreserveOriginal={(val) =>
                      setPreserveOriginals((prev) => ({
                        ...prev,
                        imageId: val,
                      }))
                    }
                    onDeleteImage={() => handleDeleteImage("imageId")}
                    isImageLoading={imageLoading["imageId"]}
                    value={withoutBackgrounds["imageId"]}
                  />
                </Col>

                <Col span={24}>
                  <ImageFieldBlock
                    key={String(withoutBackgrounds["logoId"])}
                    title="logo"
                    fieldName="logoId"
                    currentId={details?.logo?.id}
                    fieldValue={withoutBackgrounds["logoId"]}
                    currentImage={details?.logo}
                    newImageUrl={newImages["logoId"] ?? null}
                    isChanged={imageChanged["logoId"]}
                    onUpload={() => handleUploadNewImage("logoId")}
                    onImageChange={handleImageChange("logoId")}
                    onRemoveNewImage={(restoreCurrentImage) => handleRemoveNewImage("logoId", restoreCurrentImage)}
                    setWithoutBackgrounds={(val) =>
                      setWithoutBackgrounds((prev) => ({
                        ...prev,
                        logoId: val,
                      }))
                    }
                    preserveOriginal={preserveOriginals["logoId"]}
                    setPreserveOriginal={(val) =>
                      setPreserveOriginals((prev) => ({
                        ...prev,
                        logoId: val,
                      }))
                    }
                    onDeleteImage={() => handleDeleteImage("logoId")}
                    isImageLoading={imageLoading["logoId"]}
                    value={withoutBackgrounds["logoId"]}
                  />
                </Col>

                <FormProvider {...methods}>
                  <Col span={24}>
                    <FormItem label={t("map")}>
                      <Controller
                        name="map"
                        control={control}
                        rules={{ required: false }}
                        render={({ field: { ...field } }) => {
                          return (
                            <Marker
                              name={details?.name}
                              draggable={true}
                              onChange={field?.onChange}
                              location={details?.location}
                            />
                          );
                        }}
                      />
                    </FormItem>
                  </Col>
                </FormProvider>
              </Row>
            </div>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cancel")}
            </Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const StoreForm = () => {
  return (
    <StoreContextProvider>
      <Form />
    </StoreContextProvider>
  );
};
export default StoreForm;
